import type {
    SketchParametricAnalyticJacobianApplyInput,
    SketchParametricEquation,
    SketchParametricResidualEvaluation,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import type {SketchParametricParameterVector} from './SketchParametricParameterVector';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';
import {SketchParametricResidualEvaluator} from './SketchParametricResidualEvaluator';

/**
 * 草图参数方程混合雅可比计算器。
 *
 * 设计说明：
 * 1. 方程提供 `evaluateJacobian` 时使用解析导数；未提供时统一回退中心差分；
 * 2. 数值微分只评价缺少解析导数的方程，避免已迁移热路径重复计算；
 * 3. 解析导数按原始物理残差提供，本类通过统一残差评价器应用相同量纲尺度；
 * 4. 无副作用：数值扰动无论成功或异常都恢复原参数值。
 */
// ponytail: 当前仍为每轮新建二维数组；Instant2D/拖动热路径若不达 60 FPS 门禁，
// 再升级为 TypedArray 工作区复用，禁止在未测量前引入对象池复杂度。
export class SketchParametricJacobian {
    /** 中心差分步长（米/弧度）。 */
    private readonly stepSize: number;

    /** 统一残差评价器。 */
    private readonly residualEvaluator = new SketchParametricResidualEvaluator();

    /**
     * @param stepSize 中心差分步长；默认 1e-6。
     */
    public constructor(stepSize = SKETCH_PARAMETRIC_TOLERANCE.jacobianStep) {
        this.stepSize = stepSize;
    }

    /**
     * 计算残差向量。
     *
     * @param parameters 参数向量。
     * @param equations 方程列表。
     * @param characteristicLength 本轮冻结的草图特征长度。
     * @returns 拼接后的残差数组。
     */
    public collectResiduals(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        characteristicLength = parameters.calculateCharacteristicLength(),
    ): number[] {
        return [...this.evaluateResiduals(parameters, equations, characteristicLength).optimizationResiduals];
    }

    /**
     * 评价完整残差信息。
     *
     * @param parameters 参数向量。
     * @param equations 方程列表。
     * @param characteristicLength 本轮冻结的草图特征长度。
     * @returns 原始、优化及容差满足残差。
     */
    public evaluateResiduals(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        characteristicLength = parameters.calculateCharacteristicLength(),
    ): SketchParametricResidualEvaluation {
        return this.residualEvaluator.evaluate(parameters, equations, characteristicLength);
    }

    /**
     * 计算雅可比矩阵（行=残差，列=自由参数）。
     *
     * @param parameters 参数向量。
     * @param equations 方程列表。
     * @param freeParameterIds 自由参数 ID（列顺序）。
     * @param characteristicLength 本轮冻结的草图特征长度。
     * @returns 雅可比矩阵。
     */
    public compute(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        freeParameterIds: readonly string[],
        characteristicLength = parameters.calculateCharacteristicLength(),
    ): number[][] {
        /**
         * 基线评价同时校验残差行数、量纲和有限值；秩分析可独立调用 compute，
         * 不能假定调用方刚执行过残差评价。
         */
        const baseline = this.collectResiduals(parameters, equations, characteristicLength);
        const rowCount = baseline.length;
        const jacobian: number[][] = Array.from({length: rowCount}, () => (
            Array<number>(freeParameterIds.length).fill(0)
        ));
        const numericEquationsByParameterId = new Map<string, SketchParametricEquation[]>();
        const numericRowOffsetsByParameterId = new Map<string, number[]>();
        let equationRowOffset = 0;

        equations.forEach((equation) => {
            if (!equation.evaluateJacobian) {
                // parameterIds 是公式声明的完整依赖集；相同参数在局部列表重复时只注册一次。
                equation.parameterIds.forEach((parameterId, localColumnIndex) => {
                    if (equation.parameterIds.indexOf(parameterId) !== localColumnIndex) {
                        return;
                    }
                    const dependentEquations = numericEquationsByParameterId.get(parameterId) ?? [];
                    const dependentRowOffsets = numericRowOffsetsByParameterId.get(parameterId) ?? [];

                    dependentEquations.push(equation);
                    dependentRowOffsets.push(equationRowOffset);
                    numericEquationsByParameterId.set(parameterId, dependentEquations);
                    numericRowOffsetsByParameterId.set(parameterId, dependentRowOffsets);
                });
            }
            equationRowOffset += equation.residualCount;
        });

        freeParameterIds.forEach((parameterId, columnIndex) => {
            const dependentEquations = numericEquationsByParameterId.get(parameterId);
            const dependentRowOffsets = numericRowOffsetsByParameterId.get(parameterId);

            if (!dependentEquations || !dependentRowOffsets) {
                return;
            }
            const original = parameters.read(parameterId);
            let forward: number[] = [];
            let backward: number[] = [];

            try {
                parameters.write(parameterId, original + this.stepSize);
                forward = this.collectResiduals(parameters, dependentEquations, characteristicLength);

                parameters.write(parameterId, original - this.stepSize);
                backward = this.collectResiduals(parameters, dependentEquations, characteristicLength);
            } finally {
                parameters.write(parameterId, original);
            }

            let dependentRowOffset = 0;

            dependentEquations.forEach((equation, dependentEquationIndex) => {
                const targetRowOffset = dependentRowOffsets[dependentEquationIndex]!;

                for (let rowIndex = 0; rowIndex < equation.residualCount; rowIndex += 1) {
                    jacobian[targetRowOffset + rowIndex]![columnIndex] = (
                        forward[dependentRowOffset + rowIndex]!
                        - backward[dependentRowOffset + rowIndex]!
                    ) / (2 * this.stepSize);
                }
                dependentRowOffset += equation.residualCount;
            });
        });

        const freeColumnByParameterId = new Map<string, number>();

        freeParameterIds.forEach((parameterId, columnIndex) => {
            freeColumnByParameterId.set(parameterId, columnIndex);
        });
        equationRowOffset = 0;

        equations.forEach((equation) => {
            if (!equation.evaluateJacobian) {
                equationRowOffset += equation.residualCount;
                return;
            }

            const rawJacobian = equation.evaluateJacobian((parameterId) => {
                if (!equation.parameterIds.includes(parameterId)) {
                    throw new Error(
                        `方程 ${equation.equationId} 的解析 Jacobian 读取了未声明的参数依赖：`
                        + parameterId,
                    );
                }

                return parameters.read(parameterId);
            });

            this.applyAnalyticJacobian({
                equation,
                rawJacobian,
                equationRowOffset,
                characteristicLength,
                freeColumnByParameterId,
                target: jacobian,
            });
            equationRowOffset += equation.residualCount;
        });

        return jacobian;
    }

    /**
     * 校验并写入一条方程的解析 Jacobian。
     *
     * @param input 解析矩阵、行偏移、量纲尺度和目标矩阵。
     * @throws 当解析矩阵形状或数值无效时抛出可定位错误。
     */
    private applyAnalyticJacobian(input: SketchParametricAnalyticJacobianApplyInput): void {
        const {equation, rawJacobian} = input;

        if (rawJacobian.length !== equation.residualCount) {
            throw new Error(
                `方程 ${equation.equationId} 解析 Jacobian 行数不一致：`
                + `声明 ${equation.residualCount}，实际 ${rawJacobian.length}`,
            );
        }

        rawJacobian.forEach((rawRow, rowIndex) => {
            if (rawRow.length !== equation.parameterIds.length) {
                throw new Error(
                    `方程 ${equation.equationId} 解析 Jacobian 第 ${rowIndex + 1} 行列数不一致：`
                    + `声明 ${equation.parameterIds.length}，实际 ${rawRow.length}`,
                );
            }

            rawRow.forEach((rawDerivative, localColumnIndex) => {
                if (!Number.isFinite(rawDerivative)) {
                    throw new Error(
                        `方程 ${equation.equationId} 解析 Jacobian 第 ${rowIndex + 1} 行`
                        + `第 ${localColumnIndex + 1} 列不是有限数值：${String(rawDerivative)}`,
                    );
                }

                const parameterId = equation.parameterIds[localColumnIndex]!;
                const freeColumnIndex = input.freeColumnByParameterId.get(parameterId);

                if (freeColumnIndex === undefined) {
                    return;
                }

                const normalizedDerivative = this.residualEvaluator.normalizeForOptimization(
                    rawDerivative,
                    equation.residualUnits[rowIndex]!,
                    input.characteristicLength,
                );

                /** 同一参数可能在局部参数列表出现多次，解析贡献必须累加。 */
                const targetRow = input.target[input.equationRowOffset + rowIndex]!;

                targetRow[freeColumnIndex] = (targetRow[freeColumnIndex] ?? 0) + normalizedDerivative;
            });
        });
    }
}
